import { selectTheme, useAppSelector, useColorScheme } from '@libs/core';
import { PaletteCategory, TCommonData, TWidget } from '@libs/types';
import { TUseHookTheme } from '../types/hooks';
import { DEFAULT_STYLE, TCoreOptions } from '../types/options';

const drillPath = (tree: Record<string, unknown> | undefined, path: string[]): unknown => {
    if (!tree) return undefined;
    let node: unknown = tree;
    for (const key of path) {
        if (node == null || typeof node !== 'object') return undefined;
        node = (node as Record<string, unknown>)[key];
    }
    return node;
};

const isObject = (val: unknown): val is Record<string, unknown> => !!val && typeof val === 'object' && !Array.isArray(val);

// Recursively merge `source` into `target`, returning a new object.
// When a key holds a plain object on both sides, its contents are merged
// recursively; otherwise the `source` value wins (arrays and scalars are
// treated as leaves, preserving last-write-wins behaviour). `undefined`
// values in `source` are skipped so absent options never wipe theme leaves.
const mergeDeep = (target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> => {
    const output: Record<string, unknown> = { ...target };
    for (const key of Object.keys(source)) {
        const sourceVal = source[key];
        if (sourceVal === undefined) continue;
        const targetVal = target[key];
        if (isObject(sourceVal) && isObject(targetVal)) {
            output[key] = mergeDeep(targetVal, sourceVal);
        } else {
            output[key] = sourceVal;
        }
    }
    return output;
};

export const useThemeHook = <TO extends TCoreOptions>(widget: TWidget<TCommonData, TO>, defaultStyle: string = DEFAULT_STYLE): TUseHookTheme<TO> => {
    const systemMode = useColorScheme();
    const theme = useAppSelector(selectTheme);
    const currentPalette = (systemMode === 'dark' ? theme?.palette?.dark : theme?.palette?.light) ?? {};
    const { options = {} as TO } = widget;

    const styleKey = options.style as string;
    const parents = (options.parentStyleNames as string[] | undefined) ?? [];
    const path = [...parents, styleKey];

    // Resolve a palette node with a fallback chain:
    //   1. Full path [...parents, styleKey], progressively dropping the outermost ancestor.
    //   2. Parent slot only [...parents] (no styleKey), again dropping the outermost ancestor.
    // The "parent slot" fallback lets parents=[a,b] resolve to tree.a.b when no tree.a.b[styleKey] leaf exists.
    // The palette already has theme.styles baked in (see mergeStylesIntoPalette), so no merge is needed here.
    const resolveByPath = (tree: Record<string, unknown>): Record<string, unknown> | undefined => {
        let result: Record<string, unknown> | undefined;
        for (let start = 0; start <= parents.length && result === undefined; start++) {
            result = drillPath(tree, path.slice(start)) as Record<string, unknown> | undefined;
        }
        for (let start = 0; start < parents.length && result === undefined; start++) {
            result = drillPath(tree, parents.slice(start)) as Record<string, unknown> | undefined;
        }
        return result;
    };

    const widgetTheme = (resolveByPath(currentPalette) ?? currentPalette[defaultStyle]) as PaletteCategory | undefined;

    // Deep-merge the widget's options on top of the resolved theme node: option leaves overwrite
    // matching theme leaves, new keys are added, and nested palette slots (disabled/hovered/pressed/error,
    // etc.) are merged instead of being clobbered by a shallow spread.
    const palette = mergeDeep((widgetTheme ?? {}) as Record<string, unknown>, options as Record<string, unknown>) as TO;

    return { colors: theme?.colors ?? {}, palette };
};
